import { toSafeUiError, type SafeUiError } from '@/lib/user-safe-error';

export type ContactDetailFailureKind = 'unavailable' | 'auth' | 'retryable';

export interface ContactDetailFailure {
  kind: ContactDetailFailureKind;
  safeError: SafeUiError;
}

interface ErrorLike {
  code?: unknown;
  message?: unknown;
  status?: unknown;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function normalizeCrmContactId(value: string | undefined): string | undefined {
  const normalized = value?.trim();
  return normalized && UUID_PATTERN.test(normalized) ? normalized : undefined;
}

export function classifyContactDetailError(error: unknown): ContactDetailFailure {
  const errorLike =
    error && typeof error === 'object' ? error as ErrorLike : {};
  const code = typeof errorLike.code === 'string' ? errorLike.code.toUpperCase() : '';
  const message =
    typeof errorLike.message === 'string' ? errorLike.message.toLowerCase() : '';
  const status = typeof errorLike.status === 'number' ? errorLike.status : undefined;

  // Deliberately collapse absence and authorization denial into one privacy-safe state.
  if (
    code === 'PGRST116'
    || code === '42501'
    || status === 403
    || status === 404
  ) {
    return {
      kind: 'unavailable',
      safeError: toSafeUiError(error, 'not_found'),
    };
  }

  const safeError = toSafeUiError(error);
  if (
    status === 401
    || code === 'PGRST301'
    || code === 'PGRST302'
    || /\b(jwt|session|authentication)\b/.test(message)
    || safeError.kind === 'auth'
  ) {
    return {
      kind: 'auth',
      safeError: toSafeUiError(error, 'auth'),
    };
  }

  if (safeError.kind === 'not_found') {
    return { kind: 'unavailable', safeError };
  }

  return { kind: 'retryable', safeError };
}
